import { PropsWithChildren, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  getStationAllowedDomainMessage,
  isEmailDomainAllowed,
} from "use-smart-locks-shared";
import { FullSpinner } from "../../../shared/components/full-spinner";
import { authClient } from "../client/auth-client";
import { buildLoginRedirectPath } from "../client/login-redirect";
import { AuthErrorMessage } from "./auth-error-message.component";

type RequiresAuthenticationIfProps = PropsWithChildren<{
  requiresAuthentication: boolean;
  allowedEmailDomains?: string[];
  stationSlug?: string;
}>;

type AuthenticatedContentProps = PropsWithChildren<{
  allowedEmailDomains: string[];
  stationSlug?: string;
}>;

export function RequiresAuthenticationIf({
  requiresAuthentication,
  allowedEmailDomains = [],
  stationSlug,
  children,
}: RequiresAuthenticationIfProps) {
  if (!requiresAuthentication) {
    return children;
  }

  return (
    <AuthenticatedContent
      allowedEmailDomains={allowedEmailDomains}
      stationSlug={stationSlug}
    >
      {children}
    </AuthenticatedContent>
  );
}

function AuthenticatedContent({
  allowedEmailDomains,
  stationSlug,
  children,
}: AuthenticatedContentProps) {
  const location = useLocation();
  const sessionQuery = authClient.useSession();
  const user = sessionQuery.data?.user;
  const lastResolvedUserRef = useRef<typeof user>(undefined);

  if (user) {
    lastResolvedUserRef.current = user;
  }

  const effectiveUser = user ?? lastResolvedUserRef.current;

  if (sessionQuery.isPending || (sessionQuery.isRefetching && !effectiveUser)) {
    return <FullSpinner />;
  }

  if (sessionQuery.error && !effectiveUser) {
    return <AuthErrorMessage error={sessionQuery.error} />;
  }

  if (!effectiveUser) {
    return (
      <Navigate
        to={buildLoginRedirectPath(
          `${location.pathname}${location.search}`,
          stationSlug,
        )}
        replace
      />
    );
  }

  if (
    allowedEmailDomains.length > 0 &&
    !isEmailDomainAllowed(effectiveUser.email, allowedEmailDomains)
  ) {
    return (
      <AuthErrorMessage
        message={getStationAllowedDomainMessage(allowedEmailDomains)}
      />
    );
  }

  return children;
}
